/**
 * useAllTasksCompleted — rileva quando l'utente ha terminato TUTTI i task.
 *
 * "Tutti i task" e' inteso in senso globale: zero record lato backend
 * (`totalCount === 0`) e nessuna ricerca attiva. La ricerca va esclusa
 * perche' un "0 risultati" di una query non significa "hai finito tutto".
 * I filtri activeTab/filters.* sono client-side e non incidono su
 * `totalCount`, quindi non rilevano per il segnale globale.
 *
 * `justCompleted` distingue l'ISTANTE del completamento (transizione da
 * "aveva task" a "zero") dall'arrivo su una pagina gia' vuota: l'animazione
 * parte solo nel primo caso, mentre nel secondo si mostra l'ultimo frame.
 */

import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../store/hooks';

interface AllTasksCompletedResult {
  /** Nessun task residuo a livello globale (no ricerca attiva). */
  isGloballyEmpty: boolean;
  /** True solo nell'istante in cui i task passano da >0 a 0. */
  justCompleted: boolean;
}

export function useAllTasksCompleted(searchActive: boolean): AllTasksCompletedResult {
  const { status, totalCount } = useAppSelector((state) => state.tasks);

  const isGloballyEmpty = status === 'succeeded' && totalCount === 0 && !searchActive;

  // Traccia se nell'ultimo stato noto c'erano task: serve a riconoscere la
  // transizione >0 -> 0 senza far scattare l'animazione al primo caricamento
  // (totalCount parte a 0 di default) ne' a ogni rivisita di pagina vuota.
  const hadTasksRef = useRef(false);
  const [justCompleted, setJustCompleted] = useState(false);

  useEffect(() => {
    if (status !== 'succeeded') return;
    if (totalCount > 0) {
      hadTasksRef.current = true;
      setJustCompleted(false);
    } else if (isGloballyEmpty && hadTasksRef.current) {
      hadTasksRef.current = false;
      setJustCompleted(true);
    }
  }, [status, totalCount, isGloballyEmpty]);

  return { isGloballyEmpty, justCompleted };
}
